import { useMemo, useState } from "react";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { GridActionsCellItem, type GridColDef } from "@mui/x-data-grid";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import { api } from "../lib/api";
import { useAsync } from "../lib/hooks";
import { useI18n, type MessageKey } from "../lib/i18n";
import { COUNTRY_META, getCountry } from "../lib/country";
import type { AdminUser, UserRole } from "../lib/types";
import { Button, Card, ErrorBox, Field, Loading, Modal } from "../components/ui";
import { DataTable } from "../components/DataTable";
import { newestFirst } from "../lib/format";

const ROLES: UserRole[] = ["superadmin", "admin", "brand"];
const ROLE_COLOR: Record<string, "error" | "warning" | "info" | "default"> = {
  superadmin: "error",
  admin: "warning",
  brand: "info",
};

/** Nhãn role đa ngôn ngữ, an toàn kiểu (role là string tự do từ API). */
function useRoleLabel() {
  const { t } = useI18n();
  return (r: string) =>
    r === "superadmin"
      ? t("users.role.superadmin")
      : r === "admin"
        ? t("users.role.admin")
        : r === "brand"
          ? t("users.role.brand")
          : r;
}

export function Users({ meId }: { meId: number }) {
  const { t, fmtDate } = useI18n();
  const roleLabel = useRoleLabel();
  const { data, loading, error, reload } = useAsync(() => api.users.list(), []);
  const [editing, setEditing] = useState<AdminUser | "new" | null>(null);
  const [pwUser, setPwUser] = useState<AdminUser | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const users = data ?? [];

  async function run(key: string, fn: () => Promise<unknown>) {
    setBusy(key);
    setActionError(null);
    try {
      await fn();
      await reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  const columns = useMemo<GridColDef<AdminUser>[]>(
    () => [
      {
        field: "username",
        headerName: t("users.username"),
        flex: 1,
        minWidth: 150,
        renderCell: ({ row }) => (
          <Stack sx={{ lineHeight: 1.3, py: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
              {row.username}
              {row.id === meId && (
                <Typography component="span" variant="caption" color="text.secondary">
                  {" "}
                  ({t("users.you")})
                </Typography>
              )}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "role",
        headerName: t("users.role"),
        flex: 0.7,
        minWidth: 120,
        renderCell: ({ row }) => (
          <Chip
            size="small"
            color={ROLE_COLOR[row.role] ?? "default"}
            label={roleLabel(row.role)}
          />
        ),
      },
      {
        field: "brand",
        headerName: t("users.brand"),
        flex: 0.9,
        minWidth: 130,
        renderCell: ({ row }) =>
          row.role === "superadmin" || (row.role === "admin" && row.country === "br") ? (
            <Typography variant="caption" color="text.secondary">
              {t("users.allBrands")}
            </Typography>
          ) : row.brand ? (
            <Chip size="small" variant="outlined" label={row.brand} />
          ) : (
            <Typography variant="caption" color="warning.main">
              {t("users.noBrand")}
            </Typography>
          ),
      },
      {
        field: "country",
        headerName: t("table.country"),
        flex: 0.7,
        minWidth: 120,
        // superadmin quản xuyên quốc gia nên cột này không có nghĩa với họ
        renderCell: ({ row }) =>
          row.role === "superadmin" ? (
            <Typography variant="caption" color="text.secondary">
              {t("country.allCountries")}
            </Typography>
          ) : (
            <span>
              {COUNTRY_META[row.country]?.flag ?? ""} {t(`country.${row.country}` as MessageKey)}
            </span>
          ),
      },
      {
        field: "disabled",
        headerName: t("table.status"),
        flex: 0.6,
        minWidth: 110,
        renderCell: ({ row }) => (
          <Chip
            size="small"
            color={row.disabled ? "default" : "success"}
            label={row.disabled ? t("users.locked") : t("users.active")}
          />
        ),
      },
      {
        field: "createdAt",
        headerName: t("users.created"),
        flex: 0.9,
        minWidth: 130,
        renderCell: ({ row }) => (
          <Typography variant="body2" color="text.secondary" noWrap>
            {fmtDate(row.createdAt)}
          </Typography>
        ),
      },
      {
        field: "actions",
        type: "actions",
        headerName: "",
        width: 56,
        getActions: ({ row }) => [
          <GridActionsCellItem
            key="edit"
            icon={<EditOutlinedIcon fontSize="small" />}
            label={t("common.edit")}
            onClick={() => setEditing(row)}
            showInMenu
          />,
          <GridActionsCellItem
            key="pw"
            icon={<KeyOutlinedIcon fontSize="small" />}
            label={t("users.resetPw")}
            onClick={() => setPwUser(row)}
            showInMenu
          />,
          <GridActionsCellItem
            key="toggle"
            icon={<DeleteOutlineIcon fontSize="small" />}
            label={row.disabled ? t("users.unlock") : t("users.lock")}
            // Không cho tự khoá chính mình → tránh tự đá mình ra.
            disabled={row.id === meId || busy === `toggle-${row.id}`}
            onClick={() =>
              run(`toggle-${row.id}`, () =>
                api.users.update(row.id, { disabled: !row.disabled }),
              )
            }
            showInMenu
          />,
        ],
      },
    ],
    [t, fmtDate, busy, meId],
  );

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>{t("users.title")}</h1>
          <p className="muted">{t("users.subtitle")}</p>
        </div>
        <Button variant="primary" onClick={() => setEditing("new")}>
          {t("users.add")}
        </Button>
      </header>

      {error && <ErrorBox message={error} />}
      {actionError && <ErrorBox message={actionError} />}

      <Card>
        {loading ? (
          <Loading />
        ) : (
          <DataTable
            rows={newestFirst(users, "createdAt")}
            columns={columns}
            showToolbar
            loading={busy !== null}
            getRowClassName={({ row }) => (row.disabled ? "row-disabled" : "")}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          />
        )}
      </Card>

      {editing && (
        <UserForm
          user={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}

      {pwUser && (
        <PasswordModal user={pwUser} onClose={() => setPwUser(null)} />
      )}
    </div>
  );
}

/**
 * Chọn brand từ danh mục bằng select; nếu chưa có brand ưng ý thì thêm brand mới
 * ngay tại chỗ (POST /brands) rồi tự chọn. Dùng trong modal thêm/sửa user.
 */
function BrandSelect({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
}) {
  const { t } = useI18n();
  const { data, reload } = useAsync(() => api.brands.list(), []);
  const brands = data ?? [];
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function addBrand() {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    setErr(null);
    try {
      await api.brands.create({ name });
      await reload();
      onChange(name);
      setNewName("");
      setAdding(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (disabled) {
    return <input value="" disabled placeholder={t("users.allBrands")} />;
  }

  if (adding) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            style={{ flex: 1, minWidth: 0 }}
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder={t("brands.namePlaceholder")}
            autoFocus
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.preventDefault();
                void addBrand();
              }
            }}
          />
          <Button type="button" variant="primary" loading={busy} onClick={addBrand}>
            {t("brands.add2")}
          </Button>
          <Button
            type="button"
            onClick={() => {
              setAdding(false);
              setErr(null);
            }}
          >
            {t("common.cancel")}
          </Button>
        </div>
        {err && (
          <span className="field-hint" style={{ color: "var(--bad)" }}>
            {err}
          </span>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", gap: 8 }}>
      <select
        style={{ flex: 1, minWidth: 0 }}
        value={value}
        onChange={e => onChange(e.target.value)}
        required
      >
        <option value="">{t("brands.choose")}</option>
        {brands.map(b => (
          <option key={b.id} value={b.name}>
            {b.name}
          </option>
        ))}
      </select>
      <Button type="button" onClick={() => setAdding(true)}>
        + {t("brands.new")}
      </Button>
    </div>
  );
}

/** Tạo user mới (username+password+role+brand) hoặc sửa (role+brand+khoá). */
function UserForm({
  user,
  onClose,
  onSaved,
}: {
  user: AdminUser | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const roleLabel = useRoleLabel();
  const { data: rolesList } = useAsync(() => api.roles.list(), []);
  const [username, setUsername] = useState(user?.username ?? "");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<UserRole>((user?.role as UserRole) ?? "brand");
  const [brand, setBrand] = useState(user?.brand ?? "");
  const [disabled, setDisabled] = useState(user?.disabled ?? false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const availableRoles = useMemo(() => {
    const names = rolesList ? rolesList.map(r => r.name as UserRole) : ROLES;
    const set = new Set<UserRole>([...ROLES, ...names]);
    if (role) set.add(role);
    return Array.from(set);
  }, [rolesList, role]);

  const currentCountry = user?.country ?? getCountry();
  const isAdminBr = role === "admin" && currentCountry === "br";
  const needsBrand = role !== "superadmin" && !isAdminBr;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (user) {
        await api.users.update(user.id, {
          role,
          brand: needsBrand ? brand.trim() : "",
          disabled,
        });
      } else {
        await api.users.create({
          username: username.trim(),
          password,
          role,
          brand: needsBrand ? brand.trim() || null : null,
        });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={user ? t("users.formEdit", { name: user.username }) : t("users.formNew")}
      onClose={onClose}
    >
      <form onSubmit={submit} className="form">
        {error && <ErrorBox message={error} />}

        {!user && (
          <div className="form-row">
            <Field label={t("users.username")} hint={t("users.usernameHint")}>
              <input value={username} onChange={e => setUsername(e.target.value)} required minLength={3} />
            </Field>
            <Field label={t("users.password")} hint={t("users.passwordHint")}>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                minLength={6}
                autoComplete="new-password"
              />
            </Field>
          </div>
        )}

        <div className="form-row">
          <Field label={t("users.role")} hint={t("users.roleHint")}>
            <select value={role} onChange={e => setRole(e.target.value as UserRole)}>
              {availableRoles.map(r => (
                <option key={r} value={r}>
                  {roleLabel(r)}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label={t("users.brand")}
            hint={needsBrand ? t("users.brandHint") : t("users.brandNa")}
          >
            <BrandSelect value={brand} onChange={setBrand} disabled={!needsBrand} />
          </Field>
        </div>


        {user && (
          <label className="field" style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={disabled}
              onChange={e => setDisabled(e.target.checked)}
            />
            <span className="field-label" style={{ margin: 0 }}>
              {t("users.lockAccount")}
            </span>
          </label>
        )}

        <div className="form-actions">
          <Button type="button" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" variant="primary" loading={busy}>
            {user ? t("common.save") : t("users.create")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** Đặt lại mật khẩu cho 1 user. */
function PasswordModal({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const { t } = useI18n();
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.users.setPassword(user.id, password);
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t("users.resetPwTitle", { name: user.username })}
      onClose={onClose}
      footer={
        done ? (
          <Button variant="primary" onClick={onClose}>
            {t("common.close")}
          </Button>
        ) : undefined
      }
    >
      {done ? (
        <Typography variant="body2" color="success.main">
          {t("users.resetPwOk", { name: user.username })}
        </Typography>
      ) : (
        <form onSubmit={submit} className="form">
          {error && <ErrorBox message={error} />}
          <Field label={t("users.newPassword")} hint={t("users.passwordHint")}>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
            />
          </Field>
          <div className="form-actions">
            <Button type="button" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" variant="primary" loading={busy}>
              {t("users.resetPw")}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
